import type { Schemas } from '@argzombie/api-client';
import type { Bundle } from '../validate';
import { ROOM_SLOTS, type TechRequirementDef } from './types';

/*
 * The content tree (crafting.html §5 "Under the hood: the content tree (admin view)"): every item, recipe and facility as a graph,
 * tier by tier. The designers' view of the data, built from the draft. Plus the room per tier (crafting.html "The room") and a
 * reachability report. The reachability walk is PROVISIONAL until S2-TEC's `ContentValidator.Tech` ("Reachable: every item can be
 * reached from loot-table raws, tier by tier") lands; then the publish dialog shows the port of that rule.
 */

type Item = Schemas['ItemDef'];
type Recipe = Schemas['RecipeDef'];
type Module = Schemas['BaseModuleDef'];

export type TechNode = {
  key: string;
  kind: 'item' | 'facility';
  id: string;
  label: string;
  tier: number;
  category: string;
  /** Recipes that make this item (item nodes). */
  madeBy: string[];
  x: number;
  y: number;
};

export type TechEdge = {
  from: string;
  to: string;
  /** `input` = used up, `needs` = a facility or tool required (not used up). */
  kind: 'input' | 'needs';
  recipeId: string;
};

export type TechGraph = {
  nodes: TechNode[];
  edges: TechEdge[];
  tiers: number[];
  width: number;
  height: number;
};

const COL_W = 210;
const ROW_H = 30;
const NODE_W = 170;
export const TECH_NODE = { w: NODE_W, h: 22 };

/** The legacy single requirement read as one `requires` entry (the loader does this for one release). */
export function recipeRequires(r: Recipe): TechRequirementDef[] {
  const req = (r.requires ?? []).filter((x) => x != null);
  if (req.length) return req;
  return r.requiredModuleId
    ? [{ facility: r.requiredModuleId, level: r.requiredModuleLevel ?? 0 }]
    : [];
}

/** A facility's tier: its first level's tier. */
export const moduleTier = (m: Module) => m.levels?.[0]?.tier ?? 0;
/** A facility's room slot: the first level that names one. */
export const moduleSlot = (m: Module) => (m.levels ?? []).find((l) => l?.roomSlot)?.roomSlot ?? '';

export function buildTechGraph(b: Bundle, onlyTier?: number): TechGraph {
  const items = (b.items ?? []).filter((i): i is Item => !!i?.id);
  const recipes = (b.recipes ?? []).filter((r): r is Recipe => !!r?.id);
  const modules = (b.baseModules ?? []).filter((m): m is Module => !!m?.id);
  const itemById = new Map(items.map((i) => [i.id!, i]));
  const nodes = new Map<string, TechNode>();
  const add = (n: Omit<TechNode, 'x' | 'y' | 'madeBy'>) => {
    if (!nodes.has(n.key)) nodes.set(n.key, { ...n, madeBy: [], x: 0, y: 0 });
    return nodes.get(n.key)!;
  };
  const itemNode = (id: string) => {
    const i = itemById.get(id);
    return add({
      key: `i:${id}`,
      kind: 'item',
      id,
      label: i?.name || id,
      tier: i?.tier ?? 0,
      category: i?.category ?? '?',
    });
  };
  for (const m of modules)
    add({
      key: `f:${m.id}`,
      kind: 'facility',
      id: m.id!,
      label: m.name || m.id!,
      tier: moduleTier(m),
      category: 'facility',
    });
  for (const i of items) itemNode(i.id!);

  const edges: TechEdge[] = [];
  for (const r of recipes) {
    for (const out of r.outputs ?? []) {
      const o = itemNode(out.itemId ?? '');
      o.madeBy.push(r.id!);
      for (const inp of r.inputs ?? [])
        edges.push({ from: `i:${inp.itemId}`, to: o.key, kind: 'input', recipeId: r.id! });
      for (const req of recipeRequires(r)) {
        if (req.facility)
          edges.push({ from: `f:${req.facility}`, to: o.key, kind: 'needs', recipeId: r.id! });
        if (req.tool)
          edges.push({ from: `i:${req.tool}`, to: o.key, kind: 'needs', recipeId: r.id! });
      }
    }
  }
  // Facilities are built from their level costs.
  for (const m of modules)
    for (const c of m.levels?.[0]?.cost ?? [])
      edges.push({ from: `i:${c.itemId}`, to: `f:${m.id}`, kind: 'input', recipeId: '' });

  let shown = [...nodes.values()];
  if (onlyTier !== undefined) {
    // The tier's nodes plus their direct inputs and needs (one step back, not the whole chain).
    const inTier = new Set(shown.filter((n) => n.tier === onlyTier).map((n) => n.key));
    const keep = new Set(inTier);
    for (const e of edges) if (inTier.has(e.to)) keep.add(e.from);
    shown = shown.filter((n) => keep.has(n.key));
  }
  const tiers = [...new Set(shown.map((n) => n.tier))].sort((a, b) => a - b);
  const order = (n: TechNode) =>
    n.kind === 'facility'
      ? 0
      : n.category === 'workshop_tool'
        ? 1
        : n.category === 'key_part'
          ? 3
          : 2;
  let height = 0;
  tiers.forEach((t, col) => {
    const inCol = shown
      .filter((n) => n.tier === t)
      .sort(
        (a, b) =>
          order(a) - order(b) || a.category.localeCompare(b.category) || a.id.localeCompare(b.id),
      );
    inCol.forEach((n, row) => {
      n.x = 10 + col * COL_W;
      n.y = 30 + row * ROW_H;
    });
    height = Math.max(height, 30 + inCol.length * ROW_H);
  });
  const keys = new Set(shown.map((n) => n.key));
  return {
    nodes: shown,
    edges: edges.filter((e) => keys.has(e.from) && keys.has(e.to)),
    tiers,
    width: 20 + tiers.length * COL_W,
    height: height + 10,
  };
}

// ----- reachability (provisional) -------------------------------------------------------------------------------------------------

export type Reachability = {
  /** Items found in a loot table (the raws), or as a key-part piece (placed by a hunt). */
  sources: Set<string>;
  reachable: Set<string>;
  /** Items no loot table, piece or recipe chain reaches. */
  unreachable: string[];
  /** Facilities whose first level can't be paid for. */
  unbuildable: string[];
};

export function reachability(b: Bundle): Reachability {
  const items = (b.items ?? []).filter((i) => i?.id).map((i) => i.id!);
  const sources = new Set<string>();
  for (const t of b.lootTables ?? [])
    for (const e of t?.entries ?? []) if (e?.itemId) sources.add(e.itemId);
  for (const k of b.keyParts ?? [])
    for (const p of k?.pieces ?? []) if (p?.itemId) sources.add(p.itemId);
  const reachable = new Set(sources);
  const built = new Set<string>();
  const recipes = (b.recipes ?? []).filter((r): r is Recipe => !!r?.id);
  const modules = (b.baseModules ?? []).filter((m): m is Module => !!m?.id);
  for (let changed = true; changed;) {
    changed = false;
    for (const m of modules) {
      if (built.has(m.id!)) continue;
      const cost = m.levels?.[0]?.cost ?? [];
      const req = m.levels?.[0]?.requires ?? [];
      if (
        cost.every((c) => reachable.has(c.itemId ?? '')) &&
        req.every((q) => meets(q, reachable, built))
      ) {
        built.add(m.id!);
        changed = true;
      }
    }
    for (const r of recipes) {
      const outs = (r.outputs ?? []).map((o) => o.itemId ?? '');
      if (outs.every((o) => reachable.has(o))) continue;
      if (!(r.inputs ?? []).every((i) => reachable.has(i.itemId ?? ''))) continue;
      if (!recipeRequires(r).every((q) => meets(q, reachable, built))) continue;
      for (const o of outs)
        if (!reachable.has(o)) {
          reachable.add(o);
          changed = true;
        }
    }
  }
  return {
    sources,
    reachable,
    unreachable: items.filter((i) => !reachable.has(i)),
    unbuildable: modules.filter((m) => !built.has(m.id!)).map((m) => m.id!),
  };
}

/** A blueprint is an account fact, not content: it counts as met here. */
const meets = (q: TechRequirementDef, items: Set<string>, built: Set<string>) =>
  q.facility ? built.has(q.facility) : q.tool ? items.has(q.tool) : true;

// ----- the room per tier ----------------------------------------------------------------------------------------------------------

export type RoomStation = {
  slot: string;
  moduleId: string;
  label: string;
  tier: number;
  /** solid: built by this tier; outline: the next tier's gate tool; hidden: a higher tier (bare floor). */
  state: 'solid' | 'outline' | 'hidden';
  gate: boolean;
};

export type RoomView = {
  tier: number;
  stations: RoomStation[];
  /** Slots claimed by two or more facilities. */
  conflicts: string[];
  /** Facilities a recipe needs that have no room slot. */
  unslotted: string[];
  /** Slots that aren't one of the room's 12 spots. */
  unknownSlots: string[];
  toolWall: { itemId: string; label: string; state: 'ink' | 'outline' }[];
};

export function roomView(b: Bundle, tier: number): RoomView {
  const modules = (b.baseModules ?? []).filter((m): m is Module => !!m?.id);
  const gates = new Map(
    (b.techTiers ?? []).filter((t) => t?.gateModuleId).map((t) => [t.gateModuleId!, t.tier ?? 0]),
  );
  const bySlot = new Map<string, string[]>();
  const stations: RoomStation[] = [];
  for (const m of modules) {
    const slot = moduleSlot(m);
    if (!slot) continue;
    bySlot.set(slot, [...(bySlot.get(slot) ?? []), m.id!]);
    const t = moduleTier(m);
    const gateTier = gates.get(m.id!);
    // The room with every station of this tier and below built: those are solid, the next tier's gate tool is an outline.
    const state: RoomStation['state'] =
      t <= tier ? 'solid' : gateTier !== undefined && gateTier === tier + 1 ? 'outline' : 'hidden';
    stations.push({
      slot,
      moduleId: m.id!,
      label: m.name || m.id!,
      tier: t,
      state,
      gate: gateTier !== undefined,
    });
  }
  const needed = new Set<string>();
  for (const r of b.recipes ?? [])
    for (const q of r ? recipeRequires(r) : []) if (q.facility) needed.add(q.facility);
  const slotted = new Set(stations.map((s) => s.moduleId));
  const itemById = new Map((b.items ?? []).filter((i) => i?.id).map((i) => [i.id!, i]));
  return {
    tier,
    stations,
    conflicts: [...bySlot].filter(([, ids]) => ids.length > 1).map(([s]) => s),
    unslotted: [...needed].filter((f) => modules.some((m) => m.id === f) && !slotted.has(f)),
    unknownSlots: [...bySlot.keys()].filter((s) => !(ROOM_SLOTS as readonly string[]).includes(s)),
    toolWall: (b.crafting?.room?.toolWall ?? []).map((id) => {
      const i = itemById.get(id);
      return {
        itemId: id,
        label: i?.name || id,
        state: (i?.tier ?? 0) <= tier ? 'ink' : 'outline',
      };
    }),
  };
}
